import Modal from "@ui-components/Modal";
import { useState, useContext, useEffect } from "react";
import {
  TerminateModalProps,
  TerminateWorksiteSchema,
  WonWorksiteSchema,
  LostWorksiteSchema,
  IncompleteWorksiteSchema,
  TerminateReasonType,
} from "./types";
import { FormattedMessage, useIntl } from "react-intl";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import { NotificationsContext } from "@ui-components/Notifications";
import Button from "@ui-components/Button";
import { WonWorkflow } from "./modals/won-workflow";
import { LostWorkflow } from "./modals/lost-workflow";
import { IncompleteWorkflow } from "./modals/incomplete-workflow";
import authenticationService from "@services/AuthenticationService";
import GetData from "./data";
import SearchSelect from "../../../ui-components/SearchSelect";
import {MandatoryLabel} from "./MandatoryLabel";

export const WorksiteTerminateModal = ({
  closeModal,
  selectedWorksite,
  setSelectedWorksite,
}: TerminateModalProps) => {
  const userData = authenticationService.getUserData();
  const internalUser = userData.flg_internal;

  const intl = useIntl();
  const { terminateReasonOptions } = GetData(intl.formatMessage);

  const [formData, setFormData] = useState<TerminateWorksiteSchema>();
  const [wonWorksiteData, setWonWorksiteData] = useState<WonWorksiteSchema>();
  const [lostWorksiteData, setLostWorksiteData] =
    useState<LostWorksiteSchema>();
  const [incompleteWorksiteData, setIncompleteWorksiteData] =
    useState<IncompleteWorksiteSchema>();

  const [reasonType, setReasonType] = useState<TerminateReasonType>();

  const [loading, setLoading] = useState(false);

  const countryApiRequest = useCountryAxiosInstance();

  const getDisabled = (): boolean => {
    // reasonType is always required
    if (!reasonType) return true;

    switch (reasonType) {
      case "won": {
        // channel is always required
        if (!wonWorksiteData?.channel?.type) return true;
        // reason is required when channel is "Other" or "OtherKKChannel"
        if (
          (wonWorksiteData.channel.type === "Other" ||
            wonWorksiteData.channel.type === "OtherKKChannel") &&
          !("reason" in wonWorksiteData.channel && wonWorksiteData.channel.reason) && internalUser
        ) {
          return true;
        }
        return false;
      }
      case "lost": {
        // channel (lostReason) is always required
        if (!lostWorksiteData?.reason?.type) return true;
        // brand fields are required only for internal users when OtherBrand
        if (
          lostWorksiteData.reason.type === "OtherBrand"
        ) {
          const brand = "brand" in lostWorksiteData.reason ? (lostWorksiteData.reason as any).brand : undefined;
          if (!brand?.name && internalUser) return true;
        }
        if (lostWorksiteData?.reason?.type === "KKSolutionsOrProductsNotFound") {
          const whichSolutionNotFound = "which_solution_not_found" in lostWorksiteData.reason ? (lostWorksiteData.reason as any).which_solution_not_found : undefined;
          if (!whichSolutionNotFound) return true;
        }
        return false;
      }
      case "incomplete": {
        // reason has no mandatory flag, so not required
        return false;
      }
      default:
        return true;
    }
  };
  const { push } = useContext(NotificationsContext);

  useEffect(() => {
    let terminateReason = null;
    switch (reasonType) {
      case "won":
        terminateReason = wonWorksiteData;
        break;
      case "lost":
        terminateReason = lostWorksiteData;
        break;
      case "incomplete":
        terminateReason = incompleteWorksiteData;
        break;
    }
    if (terminateReason) {
      setFormData({
        terminateReason,
        terminateTimestamp: Math.trunc(new Date().getTime() / 1000),
      });
    }
  }, [wonWorksiteData, lostWorksiteData, incompleteWorksiteData]);

  const _onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Send dates and worksite id to the backend
    let _selectedWorksite = { ...selectedWorksite };
    _selectedWorksite.terminated = formData;

    let data = await countryApiRequest.post(
      `/worksites/${_selectedWorksite.id}/terminate_or_archive`,
      formData
    );
    setSelectedWorksite(data);
    push({
      title: intl.formatMessage({ id: "worksite_updated_successfully" }),
      type: "success",
    });
    setLoading(false);
    closeModal();
  };

  const isExternalUserNotCreator = !internalUser && selectedWorksite.id_creator !== userData.id;

  return (
    <Modal onExit={closeModal} submitting={loading} fixed>
      {!isExternalUserNotCreator ? <form onSubmit={_onSubmit}>
        <div className="mt-4">
          <SearchSelect
            label={
              <MandatoryLabel
                label={intl.formatMessage({
                  id: "terminate_reason_label",
                  defaultMessage: "Motivo di terminazione",
                })}
                mandatory={!reasonType}
              />
            }
            isClearable={true}
            options={terminateReasonOptions}
            value={terminateReasonOptions.find((option) => option.value === reasonType)}
            onChange={(e: any) => {
              setReasonType(e?.value);
            }}
          />
          <div className="my-4">
            {reasonType === "won" && (
              <WonWorkflow
                internalUser={internalUser}
                setWonWorksiteData={setWonWorksiteData}
              />
            )}
            {reasonType === "lost" && (
              <LostWorkflow
                internalUser={internalUser}
                setLostWorksiteData={setLostWorksiteData}
              />
            )}
            {reasonType === "incomplete" && (
              <IncompleteWorkflow
                internalUser={internalUser}
                setIncompleteWorksiteData={setIncompleteWorksiteData}
              />
            )}
          </div>
          <Button
            submitting={loading}
            disabled={getDisabled()}
            type="submit"
            className="relative overflow-hidden uppercase justify-center inline-flex items-center px-4 pb-2 pt-1.5 border border-gray-300 text-base font-semibold focus:outline-none active:opacity-70 text-white bg-black-500 border-black-500 hover:bg-am-700  w-full"
          >
            <FormattedMessage id="terminate" defaultMessage="Termina" />
          </Button>
        </div>
      </form>:
        <div className="mt-4">
          <p className="mb-4">
            <FormattedMessage
              id="archive_worksite_external_user_message"
              defaultMessage="Sei sicuro di voler archiviare questo cantiere? Se lo archivi lo troverai tra quelli terminati, potrai comunque accedervi e riattivarlo quando vorrai."
            />
          </p>
          <div className={"flex flex-row justify-center gap-4"}>
            <Button
              onClick={closeModal}
              type="button"
              styleType="white"
            >
              <FormattedMessage id="cancel" defaultMessage="Annulla"/>
            </Button>
            <Button
              submitting={loading}
              onClick={_onSubmit}
            >
              <FormattedMessage id="archive" defaultMessage="Archivia"/>
            </Button>
          </div>
        </div>
      }
    </Modal>
  );
};
